import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError, persistAuthForExternalRedirect } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/common/PageHeader";
import GlassCard from "@/components/common/GlassCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import SEOOverview from "@/components/seo/SEOOverview";
import SEOPageList from "@/components/seo/SEOPageList";
import SEOPageWorkspace from "@/components/seo/SEOPageWorkspace";
import SEODiscovery from "@/components/seo/SEODiscovery";
import SEORedirectManager from "@/components/seo/SEORedirectManager";
import {
  SEORobotsManager, SEOSitemapManager, SEOReports, SEOSettingsPanel, SEOIssuesTasks, SEOSiteFiles, SEOUrlHealth,
} from "@/components/seo/SEOOpsPanels";
import SEOLabs from "@/components/seo/SEOLabs";
import SEOAdvanced from "@/components/seo/SEOAdvanced";
import { isBrandQuery } from "@/components/seo/seoUtils";

function resolveView(pathname) {
  if (pathname === "/seo" || pathname === "/seo/") return { view: "overview" };
  if (pathname === "/seo/pages") return { view: "pages" };
  if (pathname.startsWith("/seo/pages/")) return { view: "workspace", pageId: pathname.split("/")[3] };
  if (pathname === "/seo/discover" || pathname === "/seo/add") return { view: "discover" };
  if (pathname === "/seo/redirects" || pathname === "/seo/technical/redirects") return { view: "redirects" };
  if (pathname === "/seo/site-files" || pathname.includes("/sitemap") || pathname.includes("/robots")) return { view: "site-files" };
  if (pathname === "/seo/url-health") return { view: "url-health" };
  if (pathname === "/seo/issues" || pathname.includes("/automation/tasks")) return { view: "issues" };
  if (pathname === "/seo/reports") return { view: "reports" };
  if (pathname === "/seo/labs") return { view: "labs" };
  if (pathname === "/seo/advanced") return { view: "advanced" };
  if (pathname === "/seo/settings" || pathname.includes("/automation/schedule")) return { view: "settings" };
  // Legacy deep links → pages hub
  if (pathname.startsWith("/seo/optimization") || pathname.startsWith("/seo/technical") || pathname.startsWith("/seo/schema") || pathname.startsWith("/seo/monitoring") || pathname.startsWith("/seo/automation")) {
    return { view: "pages", legacy: true };
  }
  return { view: "overview" };
}

export default function SEO() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const resolved = useMemo(() => resolveView(location.pathname), [location.pathname]);
  const view = resolved.view;
  const pageId = resolved.pageId;
  const tab = searchParams.get("tab") || "Overview";

  const [overview, setOverview] = useState(null);
  const [pages, setPages] = useState({ items: [], total: 0 });
  const [query, setQuery] = useState("");
  const [filters, setFilters] = useState({ page_type: "", source: "", status: "" });
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [addUrl, setAddUrl] = useState((process.env.REACT_APP_WEBSITE_URL || "https://www.casmediasolutions.com").replace(/\/?$/, "/"));
  const [discoverOpen, setDiscoverOpen] = useState(false);
  const [job, setJob] = useState(null);
  const [discoverSel, setDiscoverSel] = useState(new Set());
  const [running, setRunning] = useState(false);
  const [cwvRunning, setCwvRunning] = useState(false);
  const cwvLock = useRef(false);
  const [redirects, setRedirects] = useState([]);
  const [robots, setRobots] = useState(null);
  const [geo, setGeo] = useState(null);
  const [sitemap, setSitemap] = useState(null);
  const [reports, setReports] = useState(null);
  const [settings, setSettings] = useState(null);
  const [gsc, setGsc] = useState(null);
  const [issues, setIssues] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [urlHealth, setUrlHealth] = useState(null);
  const [performance, setPerformance] = useState(null);
  const [labs, setLabs] = useState(null);
  const [aiOut, setAiOut] = useState(null);
  const { user } = useAuth();
  const canPublish = ["super_admin", "admin", "seo_manager"].includes(user?.role);

  const loadOverview = async () => {
    try { setOverview((await api.get("/seo/overview")).data); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const loadPages = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/seo/pages", { params: { q: query || undefined, page_type: filters.page_type || undefined, source: filters.source || undefined, status: filters.status || undefined, page_size: 80 } });
      setPages(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    loadOverview();
    loadPages();
    api.get("/seo/redirects").then((r) => setRedirects(r.data)).catch(() => {});
    api.post("/seo/migrate").catch(() => {});
  }, []); // eslint-disable-line
  useEffect(() => { loadPages(); }, [query, filters]); // eslint-disable-line

  useEffect(() => {
    if (resolved.legacy) navigate("/seo/pages", { replace: true });
  }, [resolved.legacy]); // eslint-disable-line

  useEffect(() => {
    if (location.pathname === "/seo/add") setAddOpen(true);
    if (location.pathname === "/seo/discover") setDiscoverOpen(true);
  }, [location.pathname]);

  useEffect(() => {
    if (view === "site-files") {
      api.get("/seo/sitemap").then((r) => setSitemap(r.data)).catch(() => {});
      api.get("/seo/robots").then((r) => setRobots(r.data)).catch(() => {});
      api.get("/seo/settings").then((r) => setSettings(r.data)).catch(() => {});
      api.get("/seo/geo").then((r) => setGeo(r.data)).catch(() => setGeo(null));
    }
    if (view === "settings") api.get("/seo/settings").then((r) => setSettings(r.data)).catch(() => {});
    if (view === "settings") {
      api.get("/seo/search-console").then((r) => setGsc(r.data)).catch(() => setGsc({ connected: false }));
    }
    if (view === "reports") api.get("/seo/reports").then((r) => setReports(r.data)).catch(() => {});
    if (view === "issues") {
      api.get("/seo/issues").then((r) => setIssues(r.data)).catch(() => {});
      api.get("/seo/tasks").then((r) => setTasks(r.data)).catch(() => {});
    }
    if (view === "url-health") {
      api.get("/seo/url-health").then((r) => setUrlHealth(r.data)).catch(() => {});
      api.get("/seo/performance").then((r) => setPerformance(r.data)).catch(() => setPerformance(null));
    }
    if (view === "labs") {
      api.get("/seo/labs").then((r) => setLabs(r.data)).catch(() => setLabs(null));
      loadPages();
    }
  }, [view]);

  const runCwv = async () => {
    if (cwvLock.current) return;
    cwvLock.current = true;
    setCwvRunning(true);
    try {
      const { data } = await api.post("/seo/performance/cwv", { path: "/", strategy: "mobile" });
      if (data?.ok) {
        toast.success(`CWV updated (${data.source}): LCP ${data.lcp_ms ?? "—"}ms`);
        setPerformance((await api.get("/seo/performance")).data);
        loadOverview();
      } else {
        toast.error(data?.hint || data?.reason || data?.error || "CWV check failed");
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "CWV check failed");
    } finally {
      cwvLock.current = false;
      setCwvRunning(false);
    }
  };

  const triggerRebuild = async () => {
    try {
      const { data } = await api.post("/seo/measurement/rebuild", { force: true });
      if (data?.ok && !data?.skipped) toast.success("Cloudflare Pages rebuild triggered");
      else if (data?.skipped) toast.success(data.reason === "debounced" ? "Rebuild recently triggered (debounced)" : (data.hint || data.reason || "Skipped"));
      else toast.error(data?.hint || data?.error || data?.reason || "Rebuild failed");
      loadOverview();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Rebuild failed");
    }
  };

  const checkAlerts = async () => {
    try {
      const { data } = await api.post("/seo/measurement/check-alerts", {});
      toast.success(data?.count ? `${data.count} alert(s) updated` : "No new traffic/index alerts");
      loadOverview();
      if (view === "issues") {
        setIssues((await api.get("/seo/issues")).data);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Alert check failed");
    }
  };

  const openGscEditor = (pageId, row) => {
    const phrases = (Array.isArray(row?.queries) ? row.queries : [])
      .map((item) => (typeof item === "string" ? item : item?.query))
      .filter(Boolean);
    const explicit = (row?.query || "").trim();
    const topic = phrases.find((phrase) => !isBrandQuery(phrase)) || "";
    const params = new URLSearchParams({
      tab: "Meta",
      q: explicit || topic,
      pos: row?.position == null ? "" : String(row.position),
      clicks: row?.clicks == null ? "" : String(row.clicks),
      impr: row?.impressions == null ? "" : String(row.impressions),
    });
    phrases.slice(0, 5).forEach((phrase) => params.append("qs", phrase));
    navigate(`/seo/pages/${pageId}?${params.toString()}`);
  };

  const createGscTask = async (row) => {
    try {
      const { data } = await api.post("/seo/tasks/from-gsc", { ...row, create_issue: false });
      if (data?.page_id) {
        const queries = data.queries || [];
        const topic = (data.query || queries.find((item) => item?.query && !isBrandQuery(item.query))?.query || "");
        toast.success(topic ? `Opening ${data.path || "the page"} — add “${topic}” to the title` : `Opening ${data.path || "the page"}`);
        openGscEditor(data.page_id, { ...row, query: row.query || data.query || "", queries });
        return;
      }
      toast.error(data?.hint || "No page on this site matches that search.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not open the page");
    }
  };

  const openGscPage = async (row) => {
    try {
      const { data } = await api.post("/seo/tasks/from-gsc", { ...row, kind: "page", create_issue: false, create_task: false });
      if (data?.page_id) {
        openGscEditor(data.page_id, { ...row, query: data.query || "", queries: data.queries || [] });
        return;
      }
      toast.error(data?.hint || "That URL is not in the page list yet.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not open page");
    }
  };

  useEffect(() => {
    if (view !== "workspace" || !pageId) return;
    let cancelled = false;
    api.get(`/seo/pages/${pageId}`).then((r) => { if (!cancelled) setCurrent(r.data); }).catch(() => {
      toast.error("Page not found");
      navigate("/seo/pages");
    });
    return () => { cancelled = true; };
  }, [view, pageId]); // eslint-disable-line

  useEffect(() => {
    const flag = searchParams.get("gsc");
    if (!flag) return;
    if (flag === "connected") toast.success("Google Search Console connected");
    if (flag === "error") toast.error(`Search Console connect failed (${searchParams.get("message") || "unknown"})`);
    searchParams.delete("gsc");
    searchParams.delete("message");
    setSearchParams(searchParams, { replace: true });
    api.get("/seo/search-console").then((r) => setGsc(r.data)).catch(() => {});
    api.get("/seo/search-console/sites").then(async (r) => {
      setGsc((prev) => ({ ...(prev || {}), ...(r.data || {}) }));
      if (r.data?.connected && !r.data?.site_url && (r.data?.sites || []).length) {
        /* user must pick */
      }
    }).catch(() => {});
  }, [searchParams.get("gsc")]); // eslint-disable-line

  const loadGsc = async () => {
    try {
      const { data } = await api.get("/seo/search-console");
      setGsc(data);
      return data;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load Search Console");
      return null;
    }
  };

  const connectGsc = async () => {
    try {
      persistAuthForExternalRedirect();
      const { data } = await api.get("/seo/search-console/connect");
      if (data?.auth_url) window.location.href = data.auth_url;
      else toast.error("Connect URL missing");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Cannot start Google connect");
    }
  };

  const disconnectGsc = async () => {
    try {
      await api.post("/seo/search-console/disconnect");
      setGsc({ connected: false, queries: [], pages: [], note: "Disconnected." });
      toast.success("Search Console disconnected");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const selectGscSite = async (siteUrl) => {
    try {
      const { data } = await api.post("/seo/search-console/site", { site_url: siteUrl });
      setGsc(data);
      toast.success("Property selected");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const openPage = async (p, nextTab = "Overview") => {
    navigate(`/seo/pages/${p.id}?tab=${encodeURIComponent(nextTab)}`);
  };

  const setTab = (next) => {
    const params = new URLSearchParams(searchParams);
    params.set("tab", next);
    setSearchParams(params, { replace: true });
  };

  const savePage = async () => {
    try {
      const { data } = await api.patch(`/seo/pages/${current.id}`, {
        name: current.name, seo: current.seo, technical: current.technical, social: current.social, schema: current.schema, keywords: current.keywords, sitemap: current.sitemap, status: current.status, page_type: current.page_type, recommendations: current.recommendations,
      });
      setCurrent((prev) => ({ ...prev, ...data }));
      toast.success("Draft saved — not live until you publish");
      loadPages();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const analyze = async () => {
    setAnalyzing(true);
    try {
      const { data } = await api.post(`/seo/pages/${current.id}/analyze`);
      setCurrent((prev) => ({ ...prev, ...data }));
      toast.success("Analysis updated detected values");
      loadOverview();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setAnalyzing(false); }
  };

  const publish = async () => {
    try {
      const { data } = await api.post(`/seo/pages/${current.id}/publish`);
      setCurrent((prev) => ({ ...prev, ...data }));
      toast.success("Published to public SEO API. Site rebuild runs automatically if the deploy hook is configured.");
      loadPages();
      loadOverview();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const addPage = async () => {
    try {
      const { data } = await api.post("/seo/pages", { url: addUrl, source: "manual" });
      setAddOpen(false);
      await api.post(`/seo/pages/${data.id}/analyze`).catch(() => {});
      toast.success("URL added to SEO Management");
      loadPages();
      loadOverview();
      openPage(data, "Meta");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const startDiscovery = async (includeRemote) => {
    setRunning(true);
    setDiscoverSel(new Set());
    setJob(null);
    try {
      const { data } = await api.post("/seo/discovery", { include_remote: includeRemote });
      let final = null;
      for (let i = 0; i < 60; i += 1) {
        await new Promise((r) => setTimeout(r, 700));
        const jobRes = await api.get(`/seo/discovery/${data.id}`);
        setJob(jobRes.data);
        final = jobRes.data;
        if (["complete", "failed"].includes(jobRes.data.status)) break;
      }
      if (final?.status === "complete") {
        const news = (final.results || []).filter((r) => r.state === "new").map((r) => r.path);
        setDiscoverSel(new Set(news));
        if (news.length === 0) {
          toast.success(`Found ${(final.results || []).length} pages — all already in SEO`);
        } else {
          toast.success(`Found ${news.length} new page(s) to add`);
        }
      } else if (final?.status === "failed") {
        toast.error(final.error || "Discovery failed");
      } else {
        toast.error("Discovery is taking too long — try Admin content only");
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message || "Discovery failed");
    } finally {
      setRunning(false);
    }
  };

  const publishCore = async () => {
    if (!canPublish) { toast.error("Publishing requires SEO Manager or Admin"); return; }
    if (!window.confirm("Publish all core marketing pages to the live SEO API?")) return;
    try {
      const { data } = await api.post("/seo/publish-core", {});
      toast.success(`Published ${data.published || 0} core page(s). Rebuild website for view-source HTML.`);
      loadPages();
      loadOverview();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const publishAllDrafts = async () => {
    if (!canPublish) { toast.error("Publishing requires SEO Manager or Admin"); return; }
    const n = overview?.draft_count ?? 0;
    if (!window.confirm(`Publish all ${n} draft page(s) to live?`)) return;
    try {
      const { data } = await api.post("/seo/publish-all-drafts");
      toast.success(`Published ${data.published || 0} page(s)${data.cms_warnings ? ` (${data.cms_warnings} CMS sync warnings)` : ""}. Rebuild website for view-source HTML.`);
      loadPages();
      loadOverview();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const publishBlog = async () => {
    if (!canPublish) { toast.error("Publishing requires SEO Manager or Admin"); return; }
    if (!window.confirm("Publish blog index and all blog posts to the live SEO API?")) return;
    try {
      const { data } = await api.post("/seo/publish-blog");
      toast.success(`Published ${data.published || 0} blog URL(s). Rebuild website for view-source HTML.`);
      loadPages();
      loadOverview();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const selectAllDrafts = () => {
    const draftIds = (pages.items || []).filter((p) => p.publish_state !== "published").map((p) => p.id);
    setSelectedIds(new Set(draftIds));
  };

  const header = useMemo(() => {
    const map = {
      overview: { title: "SEO", subtitle: "Edit what Google shows for each page. One next step at a time.", actions: (
        <>
          <Button variant="ghost" className="text-white/70" onClick={() => setDiscoverOpen(true)}>Find pages</Button>
          <Button onClick={() => setAddOpen(true)} className="btn-gradient text-white border-0">Add URL</Button>
        </>
      ) },
      pages: { title: "Pages", subtitle: "Open a page → edit title & description → Publish when ready.", actions: (
        <>
          <Button variant="ghost" className="text-white/70" onClick={() => setDiscoverOpen(true)}>Find pages</Button>
          <Button onClick={() => setAddOpen(true)} className="btn-gradient text-white border-0">Add URL</Button>
        </>
      ) },
      workspace: { title: current?.name || "Edit page", subtitle: `Fix problems → edit fields → Save → Publish`, actions: (
        <Button variant="ghost" className="text-white/70" onClick={() => navigate("/seo/pages")}>Back to Pages</Button>
      ) },
      redirects: { title: "Redirects", subtitle: "Old URL → new URL (301). Rarely needed day to day.", actions: null },
      "site-files": { title: "Site files", subtitle: "Robots, sitemap, and AI briefing (llms.txt).", actions: null },
      issues: { title: "Problems", subtitle: "Open a row, edit it, and save. A fixed row leaves this list on its own.", actions: null },
      reports: { title: "Reports", subtitle: "Download a snapshot of SEO health.", actions: null },
      labs: { title: "Labs", subtitle: "Optional power tools — skip unless you need them.", actions: null },
      advanced: { title: "More tools", subtitle: "Sitemap, redirects, settings — not needed every day.", actions: null },
      settings: { title: "SEO settings", subtitle: "Site address and Google Search Console.", actions: null },
      "url-health": { title: "URL & speed", subtitle: "Technical checks and optional Core Web Vitals.", actions: null },
    };
    return map[view] || map.overview;
  }, [view, current, navigate]);

  return (
    <div className="w-full min-w-0 overflow-x-hidden">
      <PageHeader title={header.title} subtitle={header.subtitle} actions={header.actions} />

      {view === "overview" && (
        <SEOOverview
          data={overview} loading={!overview}
          onOpenPage={openPage}
          onDiscover={() => setDiscoverOpen(true)}
          onAdd={() => setAddOpen(true)}
          onIssues={() => navigate("/seo/issues")}
          onPages={() => navigate("/seo/pages")}
          onPublishAllDrafts={publishAllDrafts}
          canPublish={canPublish}
        />
      )}

      {view === "advanced" && (
        <SEOAdvanced onNavigate={(to) => navigate(to)} />
      )}

      {view === "pages" && (
        <SEOPageList
          items={pages.items} total={pages.total} loading={loading} query={query} onQuery={setQuery} filters={filters}
          onFilter={(k, v) => setFilters((f) => ({ ...f, [k]: v }))}
          onOpen={openPage}
          onAdd={() => setAddOpen(true)}
          onDiscover={() => setDiscoverOpen(true)}
          selected={selectedIds}
          onToggle={(id, on) => setSelectedIds((s) => { const n = new Set(s); on ? n.add(id) : n.delete(id); return n; })}
          onBulkAnalyze={async () => {
            if (!selectedIds.size) { toast.error("Select at least one page to analyze"); return; }
            await api.post("/seo/crawl", { page_ids: [...selectedIds] });
            toast.success("Crawl queued");
          }}
          onBulkPublish={async () => {
            if (!selectedIds.size) { toast.error("Select at least one page to publish"); return; }
            if (!canPublish) { toast.error("Publishing requires SEO Manager or Admin"); return; }
            try {
              const { data } = await api.post("/seo/pages/bulk-publish", { page_ids: [...selectedIds] });
              toast.success(`Published ${data.published || 0} page(s). Rebuild website for view-source HTML.`);
              setSelectedIds(new Set());
              loadPages();
              loadOverview();
            } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
          }}
          onPublishCore={publishCore}
          onPublishAllDrafts={publishAllDrafts}
          onPublishBlog={publishBlog}
          onSelectAllDrafts={selectAllDrafts}
          draftCount={overview?.draft_count ?? 0}
          canPublish={canPublish}
          onAnalyzeOne={async (p) => {
            try {
              await api.post(`/seo/pages/${p.id}/analyze`);
              toast.success(`Analyzed ${p.name}`);
              loadPages();
              loadOverview();
            } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
          }}
          onExport={async () => {
            const { data } = await api.get("/seo/pages", { params: { page_size: 500 } });
            const csv = ["name,path,score,type,status"].concat((data.items || []).map((p) => `${p.name},${p.path},${p.seo_score || ""},${p.page_type},${p.publish_state}`)).join("\n");
            const blob = new Blob([csv], { type: "text/csv" });
            const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "seo-pages.csv"; a.click();
          }}
        />
      )}

      {view === "workspace" && (
        current ? (
          <SEOPageWorkspace
            page={current}
            initialTab={tab}
            gscQuery={searchParams.get("q") || ""}
            gscQueries={searchParams.getAll("qs")}
            gscStats={{
              position: searchParams.get("pos") || "",
              clicks: searchParams.get("clicks") || "",
              impressions: searchParams.get("impr") || "",
            }}
            onTabChange={setTab}
            onChange={setCurrent}
            onSave={savePage}
            onAnalyze={analyze}
            onPublish={publish}
            analyzing={analyzing}
            canPublish={canPublish}
            aiOut={aiOut}
            onAi={async (action) => {
              try {
                const { data } = await api.post(
                  `/seo/pages/${current.id}/ai`,
                  { action },
                  { timeout: 45000 },
                );
                setAiOut(data);
                toast.success("Suggestions ready");
              } catch (e) {
                // Instant local outline so the editor never crashes on Network Error
                const name = current?.name || "This page";
                const fallback = {
                  action,
                  provider: "local",
                  note: "Could not reach the API — showing a local outline instead.",
                  rationale: "Edit the live page content, then tap Check page again.",
                  suggestion: action === "improve_content"
                    ? [
                        `H1: ${name}`,
                        "Open with who this page is for and the outcome.",
                        "Add 2–3 short sections (What we do / How it works / Next step).",
                        "Include a clear CTA: Contact / Start your project.",
                        "Add 2–3 internal links to Services, Portfolio, or Contact.",
                      ].join("\n• ")
                    : "Try again in a moment, or edit the related field manually.",
                };
                setAiOut(fallback);
                toast.error(formatApiError(e.response?.data?.detail) || e.message || "Suggestions unavailable — showing local tips");
              }
            }}
            onApplyAi={(patch) => {
              if (!patch || !current) return;
              setCurrent((prev) => {
                const next = { ...prev };
                if (patch.title != null) {
                  next.seo = { ...(prev.seo || {}), title: { ...((prev.seo || {}).title || {}), configured: patch.title } };
                }
                if (patch.description != null) {
                  next.seo = { ...(next.seo || prev.seo || {}), description: { ...(((next.seo || prev.seo || {}).description) || {}), configured: patch.description } };
                }
                return next;
              });
              toast.success("Applied to draft — Save, then Publish");
            }}
            onRestore={async (revisionId) => { const { data } = await api.post(`/seo/pages/${current.id}/restore`, { revision_id: revisionId }); setCurrent((prev) => ({ ...prev, ...data })); }}
            onSaveImages={async (items) => { const { data } = await api.patch(`/seo/pages/${current.id}/images`, { items }); setCurrent((prev) => ({ ...prev, ...data })); toast.success("ALT text saved as draft"); }}
            onLinkTask={async (rec) => { await api.post("/seo/tasks", { title: `Link: ${rec.anchor} → ${rec.target_url}`, page_id: current.id, notes: rec.reason }); toast.success("Task created"); }}
            onValidateSchema={async (json) => (await api.post("/seo/schema/validate", { schema: json })).data}
          />
        ) : (
          <GlassCard className="p-8 text-center space-y-3">
            <p className="text-sm text-white/50">Loading page editor…</p>
            <Button variant="ghost" className="text-white/70" onClick={() => navigate("/seo/pages")}>Back to Pages</Button>
          </GlassCard>
        )
      )}

      {view === "issues" && (
        <SEOIssuesTasks
          issues={issues} tasks={tasks}
          onOpenPage={(id) => id && navigate(`/seo/pages/${id}?tab=Meta`)}
        />
      )}

      {view === "redirects" && (
        <SEORedirectManager
          redirects={redirects}
          onAdd={async (body) => { await api.post("/seo/redirects", body); setRedirects((await api.get("/seo/redirects")).data); toast.success("Redirect added"); }}
          onDelete={async (r) => { await api.delete(`/seo/redirects/${r.id}`); setRedirects((await api.get("/seo/redirects")).data); }}
          onImport={async (csv) => { const { data } = await api.post("/seo/redirects/import", { csv }); setRedirects((await api.get("/seo/redirects")).data); toast.success(`${data.added} imported, ${data.skipped} skipped`); }}
          onExport={() => { window.open(`${api.defaults.baseURL}/seo/redirects/export`, "_blank"); }}
        />
      )}

      {view === "site-files" && (
        <SEOSiteFiles
          robots={robots} sitemap={sitemap} productionOrigin={settings?.production_origin || overview?.production_origin}
          geo={geo}
          onSaveRobots={async (body) => {
            const res = await api.patch("/seo/robots", body);
            setRobots(res.data);
            const origin = (settings?.production_origin || process.env.REACT_APP_WEBSITE_URL || "").replace(/\/$/, "");
            toast.success(`Robots saved — live at ${origin}/robots.txt within ~60s`);
          }}
          onToggleSitemap={async (id, include) => { await api.patch("/seo/sitemap", { pages: [{ id, include }] }); setSitemap((await api.get("/seo/sitemap")).data); }}
          onSaveGeo={async (body) => {
            const res = await api.patch("/seo/geo", body);
            setGeo(res.data);
            toast.success("GEO settings saved");
          }}
          onPingIndexNow={async (paths) => {
            const res = await api.post("/seo/geo/indexnow/ping", { paths });
            setGeo((await api.get("/seo/geo")).data);
            toast.success(res.data?.ok ? "IndexNow ping sent" : `IndexNow: ${res.data?.reason || res.data?.error || "check status"}`);
          }}
        />
      )}

      {view === "url-health" && (
        <SEOUrlHealth data={urlHealth} performance={performance} onRunCwv={runCwv} cwvRunning={cwvRunning} />
      )}

      {view === "reports" && (
        <SEOReports data={reports} onGenerate={async (format) => {
          const res = await api.post("/seo/reports", { format }, { responseType: format === "json" ? "json" : "blob" });
          if (format === "csv" || format === "pdf") {
            const blob = new Blob([res.data], { type: format === "pdf" ? "application/pdf" : "text/csv" });
            const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `seo-report.${format}`; a.click();
            toast.success(`${format.toUpperCase()} downloaded`);
          } else { setReports(res.data); toast.success("Report generated"); }
        }} />
      )}

      {view === "labs" && (
        <SEOLabs
          data={labs}
          pages={pages.items || []}
          onRefresh={async () => {
            try { setLabs((await api.get("/seo/labs")).data); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
          }}
          onCreateSite={async (body) => {
            try {
              await api.post("/seo/sites", body);
              setLabs((await api.get("/seo/labs")).data);
              toast.success("Site added");
            } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
          }}
          onActivateSite={async (id) => {
            try {
              await api.post(`/seo/sites/${id}/activate`);
              setLabs((await api.get("/seo/labs")).data);
              toast.success("Active site updated (production origin aligned)");
              loadOverview();
            } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
          }}
          onCreateExperiment={async (body) => {
            try {
              await api.post("/seo/experiments", body);
              setLabs((await api.get("/seo/labs")).data);
              toast.success("Experiment created");
            } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
          }}
          onApplyVariant={async (experimentId, variantId) => {
            try {
              await api.post(`/seo/experiments/${experimentId}/apply`, { variant_id: variantId });
              setLabs((await api.get("/seo/labs")).data);
              toast.success("Variant applied to page draft — Publish when ready");
            } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
          }}
          onSyncExperiment={async (experimentId) => {
            try {
              const { data } = await api.post(`/seo/experiments/${experimentId}/sync-gsc`);
              setLabs((await api.get("/seo/labs")).data);
              toast.success(data?.ok ? "GSC metrics attached to active variant" : (data?.note || data?.reason || "No GSC match"));
            } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
          }}
          onAddCompetitor={async (body) => {
            try {
              await api.post("/seo/competitors", body);
              setLabs((await api.get("/seo/labs")).data);
              toast.success("Competitor row added");
            } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
          }}
          onFetchCompetitors={async (body) => {
            try {
              const { data } = await api.post("/seo/competitors/fetch", body);
              setLabs((await api.get("/seo/labs")).data);
              if (data?.skipped) toast.error(data.hint || data.reason || "SERP API not configured");
              else toast.success(`Fetched ${data?.items?.length || 0} SERP rows`);
            } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
          }}
          onAddCitation={async (body) => {
            try {
              await api.post("/seo/citations", body);
              setLabs((await api.get("/seo/labs")).data);
              toast.success("Citation logged");
            } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
          }}
          onProbeCitation={async (body) => {
            try {
              const { data } = await api.post("/seo/citations/probe", body);
              setLabs((await api.get("/seo/labs")).data);
              if (data?.ok) toast.success(data.mentioned ? "Gemini would cite you" : "Gemini would likely skip you");
              else toast.error(data?.hint || data?.reason || data?.error || "Probe failed");
            } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
          }}
        />
      )}

      {view === "settings" && (
        <SEOSettingsPanel
          data={settings}
          gsc={gsc}
          measurement={overview?.measurement}
          onSave={async (body) => { setSettings((await api.patch("/seo/settings", body)).data); toast.success("Settings saved"); loadOverview(); }}
          onCrawl={async () => { await api.post("/seo/crawl", {}); toast.success("Full site crawl queued"); }}
          onConnectGsc={connectGsc}
          onDisconnectGsc={disconnectGsc}
          onSelectGscSite={selectGscSite}
          onRefreshGsc={loadGsc}
          onCreateGscTask={createGscTask}
          onOpenGscPage={openGscPage}
          onRunCwv={runCwv}
          cwvRunning={cwvRunning}
          onRebuild={triggerRebuild}
          onCheckAlerts={checkAlerts}
        />
      )}

      <Dialog open={addOpen} onOpenChange={(o) => { setAddOpen(o); if (!o && location.pathname === "/seo/add") navigate("/seo/pages"); }}>
        <DialogContent className="glass-strong border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>Add SEO Page</DialogTitle>
            <DialogDescription className="text-white/60">Paste any production URL on casmediasolutions.com. It does not need to exist in the CMS.</DialogDescription>
          </DialogHeader>
          <Input value={addUrl} onChange={(e) => setAddUrl(e.target.value)} className="bg-black/20 border-white/10 text-white" />
          <Button onClick={addPage} className="btn-gradient text-white border-0">Analyze URL and add</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={discoverOpen} onOpenChange={(o) => { setDiscoverOpen(o); if (!o && location.pathname === "/seo/discover") navigate("/seo/pages"); if (!o) setJob(null); }}>
        <DialogContent className="glass-strong border-white/10 text-white w-[calc(100vw-1.5rem)] max-w-4xl max-h-[90vh] overflow-y-auto sm:w-full">
          <DialogHeader>
            <DialogTitle>Find website pages</DialogTitle>
            <DialogDescription className="text-white/60">
              Step 1: scan for URLs. Step 2: add any new ones. Step 3: edit titles on Pages.
            </DialogDescription>
          </DialogHeader>
          <SEODiscovery
            job={job}
            running={running}
            onStart={startDiscovery}
            onDone={() => {
              setDiscoverOpen(false);
              navigate("/seo/pages");
            }}
            onImport={async () => {
              try {
                const { data } = await api.post("/seo/discovery/import", { paths: [...discoverSel] });
                toast.success(`Added ${data.added || 0} page(s) to SEO`);
                setDiscoverSel(new Set());
                setDiscoverOpen(false);
                setJob(null);
                loadPages();
                loadOverview();
                navigate("/seo/pages");
              } catch (e) {
                toast.error(formatApiError(e.response?.data?.detail) || "Could not add pages");
              }
            }}
            selected={discoverSel}
            onSelectPaths={(paths) => setDiscoverSel(new Set(paths))}
            onToggle={(path, on) => setDiscoverSel((s) => { const n = new Set(s); on ? n.add(path) : n.delete(path); return n; })}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
